import { createSelector } from 'reselect';

import { ProjectType, ReportFormatDetails } from '../../components/projects/generic/ReportGenerationV2/utils';
import type { AttributionReportIncludeOptions as ReportOptions } from '../../../shared/reports';
import type { ReduxStore } from '../store';

import type { ReportGenerationStore } from './types';

export const getReportGenerationStore = (state: ReduxStore) => state.reportGeneration;
const createReportGenerationSelector = <R>(selectorFn: (state: ReportGenerationStore) => R) =>
  createSelector(getReportGenerationStore, selectorFn);

/**
 * Get current project type
 */
export const getProjectType = createReportGenerationSelector(
  (state: ReportGenerationStore) => state.projectType || ProjectType.Project,
);
/**
 * Get current category
 */
export const getCategory = createReportGenerationSelector((state: ReportGenerationStore) => state.category);
/**
 * Get current format
 */
export const getFormat = createReportGenerationSelector((state: ReportGenerationStore) => state.format);
/**
 * Get current project context - either project or release group
 */
const getCurrentProjectContext = createSelector([
  getReportGenerationStore,
  getProjectType,
], (state, projectType) => state.projectContext[projectType]);
/**
 * Get Dependency Info Options (BOM Columns)
 */
export const getDependencyInfoOptions = createReportGenerationSelector(
  (state: ReportGenerationStore) => state.dependencyInfoOptions);
/**
 * Get all report option values
 */
export const getOptions = createReportGenerationSelector((state: ReportGenerationStore) => state.options);
/**
 * Get a single report option value
 * @param {keyof ReportOptions} option - Option to get from the store
 */
export const getOption = (option: keyof ReportOptions) => createSelector(getOptions, options => options[option]);
/**
 * Get report context
 */
export const getReportContext = createSelector([
  getProjectType,
  getCategory,
  getCurrentProjectContext,
], (projectType, category, projectContext) => ({
  projectType,
  category,
  ...projectContext,
}));

/**
 * Get custom header text
 */
export const getCustomHeaderText = createReportGenerationSelector(
  (state: ReportGenerationStore) => state.customHeaderText);

// Async action selectors
/**
 * Get all asyncStatuses
 */
export const getAsyncStatuses = createReportGenerationSelector((state: ReportGenerationStore) => state.asyncStatus);
/**
 * Get fetch public report url status
 */
export const getFetchPublicReportUrlStatus = createSelector(
  getAsyncStatuses,
  statuses => statuses.fetchPublicReportUrl,
);
/**
 * Get public report url
 */
export const getPublicReportUrl = createReportGenerationSelector(
  (state: ReportGenerationStore) => state.publicReportUrl,
);
/**
 * Get queue email report status
 */
export const getQueueEmailReportStatus = createSelector(
  getAsyncStatuses,
  statuses => statuses.queueEmailReport,
);
/**
 * Get queue publish report status
 */
export const getQueuePublishReportStatus = createSelector(
  getAsyncStatuses,
  statuses => statuses.queuePublishReport,
);

/**
 * Get custom text update status
 */
export const getUpdateCustomTextStatus = createSelector(
  getAsyncStatuses,
  statuses => statuses.updateCustomText,
);

/**
 * Get update dependency info options status
 */
export const getDependencyInfoOptionStatus = createSelector(
  getAsyncStatuses,
  statuses => statuses.updateDependencyInfoOptions,
);

/**
 * Filters the list of formats based on the current
 * category and projectType.
 */
export const getAvailableFormats = createSelector([
  getCategory,
  getProjectType,
], (category, projectType) =>
  Object.values(ReportFormatDetails)
    .filter(({ type: reportCategory, supportsReleaseGroups }) => {
      if (projectType === 'releaseGroup' && !supportsReleaseGroups) {
        return false;
      }
      return reportCategory.includes(category);
    }),
);
